import SplitBilling from "@/components/Orders/SplitBilling"
import usePaymentMethods from "@/hooks/usePaymentMethods"
import { useAppDispatch, useAppSelector } from "@/lib/hooks"
import {
  FormControl,
  FormHelperText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material"
import { useState, useEffect } from "react"
import {
  setSplitBilling,
  setPaymentMethod,
  setSplitPayments,
  setCashReceived,
} from "@/redux/slices/orderFormSlice"

export function OrderPaymentMethod({
  controller,
  control,
  errors,
  register,
  watch,
  setValue,
}) {
  const Controller = controller
  const { paymentMethods, isLoading: paymentLoading } = usePaymentMethods()
  const dispatch = useAppDispatch()
  const { splitBilling, paymentMethod, change, cashReceived } = useAppSelector(
    (state) => state.order
  )

  const methods = paymentMethods?.data || []
  const selectedMethod = methods.find((method) => method.id === paymentMethod)
  const isCash = selectedMethod?.value?.trim().toLowerCase() === "cash"

  useEffect(() => {
    if (!splitBilling && methods.length && !selectedMethod) {
      const defaultMethod =
        methods.find(
          (method) => method.value?.trim().toLowerCase() === "cash"
        ) || methods[0]
      dispatch(setPaymentMethod(defaultMethod.id))
    }
  }, [methods, selectedMethod, splitBilling, dispatch])

  // Sync form value with Redux state when paymentMethod changes
  useEffect(() => {
    if (paymentMethod && !splitBilling && setValue) {
      // Use setTimeout to avoid updating during render
      const timeoutId = setTimeout(() => {
        setValue("payment_method", paymentMethod, { shouldValidate: false })
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [paymentMethod, splitBilling, setValue])

  return (
    <div className="flex rounded-2xl bg-white flex-col shadow-sm flex-1">
      <Typography
        variant="h6"
        className="font-semibold mb-2 flex justify-between mr-4"
      >
        <div> Payment Method</div>
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={splitBilling}
                onChange={(e) => dispatch(setSplitBilling(e.target.checked))}
                color="primary"
              />
            }
            label="Split Bill"
          />
        </div>
      </Typography>

      {!splitBilling ? (
        <div className="flex justify-between flex-1">
          {paymentLoading ? (
            <div className="flex justify-center py-4">
              <CircularProgress size={24} />
            </div>
          ) : (
            <FormControl>
              <Controller
                name="payment_method"
                control={control}
                render={({ field: { onChange, value } }) => {
                  // Use Redux state value if form value is not set, or sync them
                  const currentValue = value || paymentMethod || null

                  return (
                    <>
                      <ToggleButtonGroup
                        fullWidth
                        exclusive
                        value={currentValue || ""}
                        onChange={(event, newValue) => {
                          if (newValue === null) return
                          const methodId = Number(newValue)
                          dispatch(setPaymentMethod(methodId))
                          onChange(methodId)
                        }}
                        aria-label="payment method toggle"
                        className="flex flex-wrap"
                      >
                        {paymentMethods?.data?.map((method) => (
                          <ToggleButton
                            key={method.id}
                            value={method.id}
                            color={currentValue === method.id ? "primary" : ""}
                            className="flex-1 py-2 text-sm font-medium"
                          >
                            {method.display_name}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>

                      {errors.payment_method && (
                        <FormHelperText error>
                          {errors.payment_method.message}
                        </FormHelperText>
                      )}
                    </>
                  )
                }}
              />
            </FormControl>
          )}

          {isCash && (
            <div className="mt-4 place-self-end">
              <TextField
                label="Cash Received"
                name="cash_received"
                type="number"
                size="small"
                required
                value={cashReceived}
                onChange={(event) =>
                  dispatch(setCashReceived(Number(event.target.value)))
                }
              />

              <div className="rounded-lg  p-2 text-sm text-gray-700 flex justify-between">
                {change >= 0 ? (
                  <span>Return Change:</span>
                ) : (
                  <span>Insufficient Cash:</span>
                )}
                <span
                  className={change < 0 ? "text-red-600" : "text-green-600"}
                >
                  {change >= 0
                    ? `Rs. ${change.toFixed(2)}`
                    : `Rs. ${Math.abs(change).toFixed(2)}`}
                </span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <SplitBilling />
      )}
    </div>
  )
}
